import { useState } from 'react';
import { attemptSilentReauth } from '@/lib/auth/reauth';
import { redirectToLogin } from '@/lib/auth/redirectToLogin';
import { cn } from '@/lib/utils';
import { SessionOverlay } from './session-overlay';

interface SessionExpiredOverlayProps {
  /** Whether the overlay is visible */
  open: boolean;
  /**
   * Logout URL from the server, if any. When present, used as the Sign in
   * target so the user lands at the same SSO/IdP flow they came from.
   * Otherwise the button redirects to the control plane root.
   */
  logoutUrl?: string;
  /**
   * Called when the session was restored in place, so the host can dismiss the
   * overlay and refetch. Absent means the overlay can only redirect.
   */
  onRestored?: () => void;
}

/**
 * Full-screen modal overlay shown when the user's session has expired
 * and token refresh has failed. Non-dismissible — the user clicks Sign in
 * to return to the control plane.
 */
export function SessionExpiredOverlay({ open, logoutUrl, onRestored }: SessionExpiredOverlayProps) {
  const [busy, setBusy] = useState(false);

  const handleSignIn = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Cheapest outcome first: the control-plane session usually outlives the
      // workspace one, so a session can often be restored without navigating and
      // without losing editor state.
      const result = await attemptSilentReauth();
      if (result === 'ok' && onRestored) {
        onRestored();
        return;
      }
      redirectToLogin('session-expired');
    } finally {
      // Unconditional: the overlay is mounted permanently (see RootLayout), so
      // any outcome that falls through to redirectToLogin — including a
      // misconfigured deploy where it returns false without navigating — must
      // still release the button. Otherwise Sign in silently locks up until a
      // full page reload.
      setBusy(false);
    }
  };

  const buttonClass = cn(
    'inline-flex items-center justify-center',
    'rounded-md px-6 py-2 text-sm font-medium',
    'bg-primary text-primary-foreground',
    'hover:bg-primary/90 transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-offset-2'
  );

  return (
    <SessionOverlay
      open={open}
      title="Session expired"
      description="Your session has expired. Sign in again to continue."
      action={
        logoutUrl ? (
          <a href={logoutUrl} className={buttonClass}>
            Sign in
          </a>
        ) : (
          <button type="button" className={buttonClass} onClick={handleSignIn}>
            Sign in
          </button>
        )
      }
    />
  );
}

export default SessionExpiredOverlay;
